/**
 * ScenarioProjectionsBase - Unified Scenario Projections Component
 * 
 * Consolidates ScenarioProjections.tsx and ScenarioProjectionsAccountant.tsx
 * into a single reusable component that accepts a token getter function.
 * 
 * Usage:
 * - For regular users (Clerk auth): Pass getToken from useAuth()
 * - For accountants: Pass token directly from AccountantAuthContext
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sliders, TrendingUp, TrendingDown, BarChart3, Save, Trash2, AlertCircle, HelpCircle, Shield, ChevronDown } from 'lucide-react';
import formatCurrency from '../utils/formatCurrency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Info tooltip component for explanations
const InfoTooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help inline" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 text-xs text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg -left-28 top-6">
      {text}
      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-white border-l border-t border-gray-200 rotate-45"></div>
    </div>
  </div>
);

interface ScenarioProjectionsBaseProps {
  reportId: string;
  baseResult: any;
  // Token can be provided either as a getter function (Clerk) or direct string (Accountant)
  getToken?: () => Promise<string | null>;
  token?: string | null;
}

export default function ScenarioProjectionsBase({ 
  reportId, 
  baseResult, 
  getToken,
  token: directToken
}: ScenarioProjectionsBaseProps) {
  // Base values from report
  const baseIndemnity = baseResult?.indemnity_period_days || 90;
  const baseClaim = baseResult?.final_claim_amount || 0;
  const baseDailyGP = baseResult?.daily_gross_profit || 0;
  
  // Scenario adjustments
  const [indemnityDays, setIndemnityDays] = useState(baseIndemnity);
  const [revenueDropPct, setRevenueDropPct] = useState(0);
  const [expenseReductionPct, setExpenseReductionPct] = useState(0);
  
  // Results
  const [scenarioResult, setScenarioResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  // Mitigation analysis state
  const [mitigationExpanded, setMitigationExpanded] = useState(false);
  const [marketingMaintained, setMarketingMaintained] = useState(false);
  const [tempRelocation, setTempRelocation] = useState(false);
  const [altRevenue, setAltRevenue] = useState(false);
  const [expenseReduction, setExpenseReduction] = useState(false);
  const [resumeTimeline, setResumeTimeline] = useState<string>('significantly_delayed');
  const [mitigationResult, setMitigationResult] = useState<any>(null);
  const [mitigationLoading, setMitigationLoading] = useState(false);
  const [mitigationError, setMitigationError] = useState<string | null>(null);

  // Get auth token (supports both Clerk and Accountant auth)
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (directToken) {
      return directToken;
    }
    if (getToken) {
      return await getToken();
    }
    return null;
  }, [directToken, getToken]);

  // Calculate scenario function
  const calculateScenario = useCallback(async () => {
    if (!reportId) {
      console.warn('❌ No report ID provided');
      return;
    }
    
    // Wait for token if using accountant auth
    if (!directToken && !getToken) {
      console.log('⏳ No auth method provided');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const token = await getAuthToken();
      
      if (!token) {
        console.log('⏳ Waiting for authentication token...');
        return;
      }
      
      console.log('📊 Calculating scenario with:', {
        reportId,
        indemnityDays,
        revenueDropPct,
        expenseReductionPct,
        backendUrl: BACKEND_URL
      });
      
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/scenarios/calculate/${reportId}`,
        {
          indemnity_period_days: indemnityDays,
          revenue_drop_pct: revenueDropPct,
          expense_reduction_pct: expenseReductionPct
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('✅ Scenario result:', response.data);
      
      // Extract scenario data from the response
      const scenarioData = response.data.scenario || response.data;
      const comparison = scenarioData.comparison || {};
      const scenarioResults = scenarioData.scenario_results || {};
      
      // Build a normalized result object for the UI
      const normalizedResult = {
        projected_claim: comparison.scenario_claim_amount || scenarioResults.final_claim_amount || 0,
        daily_gross_profit: comparison.scenario_daily_gp || scenarioResults.daily_gross_profit || 0,
        revenue_adjustment: scenarioResults.business_revenue ? 
          (scenarioResults.business_revenue - (response.data.scenario?.scenario_results?.business_revenue || 0)) : 0,
        expense_savings: scenarioResults.adjustments_applied?.expense_reduction_pct ? 
          (scenarioResults.variable_expenses * (scenarioResults.adjustments_applied.expense_reduction_pct / 100)) : 0,
        method: scenarioResults.configuration?.config_source || 'Linear',
        sensitivity_analysis: scenarioData.sensitivity_analysis,
        but_for_projection: scenarioData.but_for_projection,
        comparison: comparison
      };
      
      setScenarioResult(normalizedResult);
    } catch (err: any) {
      console.error('❌ Error calculating scenario:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to calculate scenario');
    } finally {
      setLoading(false);
    }
  }, [reportId, indemnityDays, revenueDropPct, expenseReductionPct, getAuthToken, directToken, getToken]);

  // Calculate mitigation credit
  const calculateMitigation = useCallback(async () => {
    if (!reportId) return;
    if (!directToken && !getToken) return;

    try {
      setMitigationLoading(true);
      setMitigationError(null);
      const token = await getAuthToken();
      if (!token) return;

      const response = await axios.post(
        `${BACKEND_URL}/api/v1/mitigation/calculate/${reportId}`,
        {
          mitigation_factors: {
            marketing_maintained: marketingMaintained,
            temp_relocation: tempRelocation,
            alt_revenue: altRevenue,
            expense_reduction: expenseReduction,
            resume_timeline: resumeTimeline,
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMitigationResult(response.data.mitigation_credit);
    } catch (err: any) {
      console.error('Mitigation calculation error:', err);
      setMitigationError(
        err.response?.data?.detail || err.message || 'Failed to calculate mitigation credit'
      );
    } finally {
      setMitigationLoading(false);
    }
  }, [reportId, marketingMaintained, tempRelocation, altRevenue, expenseReduction, resumeTimeline, getAuthToken, directToken, getToken]);

  // Load existing mitigation credit on mount
  useEffect(() => {
    if (!reportId) return;
    if (!directToken && !getToken) return;

    const loadExisting = async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;

        const response = await axios.get(
          `${BACKEND_URL}/api/v1/mitigation/${reportId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.mitigation_credit) {
          setMitigationResult(response.data.mitigation_credit);
          // Restore factor state from saved inputs
          const inputs = response.data.mitigation_credit.inputs?.mitigation_factors;
          if (inputs) {
            setMarketingMaintained(inputs.marketing_maintained ?? false);
            setTempRelocation(inputs.temp_relocation ?? false);
            setAltRevenue(inputs.alt_revenue ?? false);
            setExpenseReduction(inputs.expense_reduction ?? false);
            setResumeTimeline(inputs.resume_timeline ?? 'significantly_delayed');
          }
        }
      } catch {
        // No saved mitigation - that's fine
      }
    };

    loadExisting();
  }, [reportId, directToken, getToken, getAuthToken]);

  // Auto-calculate mitigation when factors change (debounced)
  useEffect(() => {
    if (!mitigationExpanded) return;
    if (!directToken && !getToken) return;

    const timer = setTimeout(() => {
      calculateMitigation();
    }, 600);

    return () => clearTimeout(timer);
  }, [marketingMaintained, tempRelocation, altRevenue, expenseReduction, resumeTimeline, mitigationExpanded, calculateMitigation, directToken, getToken]);

  // Auto-calculate when parameters change
  useEffect(() => {
    if (directToken === null && !getToken) {
      return; // Wait for token
    }

    const timer = setTimeout(() => {
      if (expanded) {
        calculateScenario();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [indemnityDays, revenueDropPct, expenseReductionPct, expanded, calculateScenario, directToken, getToken]);

  // Calculate claim change
  const claimChange = scenarioResult?.projected_claim 
    ? scenarioResult.projected_claim - baseClaim
    : 0;
  const changePercent = baseClaim > 0 
    ? ((claimChange / baseClaim) * 100).toFixed(1)
    : '0';

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-indigo-200 mt-6">
      {/* Header */}
      <div 
        className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-4 cursor-pointer hover:from-indigo-600 hover:to-indigo-700 transition-all"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Sliders className="w-6 h-6 text-white" />
            <div>
              <h3 className="text-lg font-bold text-white">Loss Projection Scenarios</h3>
              <p className="text-indigo-100 text-sm">What-If Analysis for Settlement Negotiations</p>
            </div>
          </div>
          <svg
            className={`w-6 h-6 text-white transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Calculation Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Parameter Sliders */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Indemnity Period */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <TrendingUp className="w-4 h-4 mr-2 text-indigo-500" />
                Indemnity Period (Days)
                <InfoTooltip text="The number of days the business was interrupted. Longer periods typically result in higher claims." />
              </label>
              <input
                type="range"
                min="30"
                max="365"
                value={indemnityDays}
                onChange={(e) => setIndemnityDays(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>30 days</span>
                <span className="text-indigo-600 font-semibold">{indemnityDays} days</span>
                <span>365 days</span>
              </div>
            </div>

            {/* Revenue Drop */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <TrendingDown className="w-4 h-4 mr-2 text-red-500" />
                Additional Revenue Drop (%)
                <InfoTooltip text="Extra revenue decline beyond what's already captured. Use for conservative scenarios." />
              </label>
              <input
                type="range"
                min="-50"
                max="50"
                value={revenueDropPct}
                onChange={(e) => setRevenueDropPct(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>-50%</span>
                <span className={`font-semibold ${revenueDropPct < 0 ? 'text-green-600' : revenueDropPct > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                  {revenueDropPct > 0 ? '+' : ''}{revenueDropPct}%
                </span>
                <span>+50%</span>
              </div>
            </div>

            {/* Expense Reduction */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <BarChart3 className="w-4 h-4 mr-2 text-green-500" />
                Variable Expense Reduction (%)
                <InfoTooltip text="Estimated expense savings during the interruption period (e.g., reduced labor, supplies)." />
              </label>
              <input
                type="range"
                min="0"
                max="50"
                value={expenseReductionPct}
                onChange={(e) => setExpenseReductionPct(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span className="text-green-600 font-semibold">{expenseReductionPct}%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          {/* Results Display */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Base Claim */}
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 mb-1">Base Claim</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(baseClaim)}
                </p>
              </div>

              {/* Projected Claim */}
              <div className="bg-indigo-50 rounded-lg p-4 text-center border-2 border-indigo-200">
                <p className="text-sm text-indigo-600 mb-1">Projected Claim</p>
                {loading ? (
                  <div className="flex justify-center items-center h-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-indigo-700">
                    {formatCurrency(scenarioResult?.projected_claim || baseClaim)}
                  </p>
                )}
              </div>

              {/* Change */}
              <div className={`rounded-lg p-4 text-center ${
                claimChange >= 0 ? 'bg-green-50' : 'bg-red-50'
              }`}>
                <p className="text-sm text-gray-600 mb-1">Change</p>
                <p className={`text-2xl font-bold ${
                  claimChange >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {claimChange >= 0 ? '+' : ''}{formatCurrency(claimChange)}
                  <span className="text-sm ml-1">({changePercent}%)</span>
                </p>
              </div>
            </div>
          </div>

          {/* Scenario Details */}
          {scenarioResult && (
            <div className="mt-4 bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Scenario Breakdown</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Daily Gross Profit:</span>
                  <span className="ml-2 font-semibold">
                    {formatCurrency(scenarioResult.daily_gross_profit || baseDailyGP)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Revenue Adjustment:</span>
                  <span className="ml-2 font-semibold">
                    {formatCurrency(scenarioResult.revenue_adjustment || 0)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Expense Savings:</span>
                  <span className="ml-2 font-semibold text-green-600">
                    {(scenarioResult.expense_savings || 0) > 0 
                      ? `-${formatCurrency(Math.abs(scenarioResult.expense_savings))}` 
                      : formatCurrency(0)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Projection Method:</span>
                  <span className="ml-2 font-semibold text-indigo-600">
                    {scenarioResult.method || 'Linear'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Quick Presets */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm text-gray-600">Quick Presets:</span>
            <button
              onClick={() => {
                setIndemnityDays(baseIndemnity);
                setRevenueDropPct(0);
                setExpenseReductionPct(0);
              }}
              className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-full transition"
            >
              Reset to Base
            </button>
            <button
              onClick={() => {
                setIndemnityDays(Math.round(baseIndemnity * 1.5));
                setRevenueDropPct(10);
                setExpenseReductionPct(5);
              }}
              className="px-3 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-full transition"
            >
              Conservative
            </button>
            <button
              onClick={() => {
                setIndemnityDays(Math.round(baseIndemnity * 2));
                setRevenueDropPct(20);
                setExpenseReductionPct(10);
              }}
              className="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-800 rounded-full transition"
            >
              Aggressive
            </button>
            <button
              onClick={() => {
                setIndemnityDays(365);
                setRevenueDropPct(-10);
                setExpenseReductionPct(20);
              }}
              className="px-3 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-full transition"
            >
              Maximum Coverage
            </button>
          </div>

          {/* ─── Mitigation Analysis Section ─── */}
          <div className="mt-6 border-t border-gray-200 pt-6">
            <div
              className="flex items-center justify-between cursor-pointer group"
              onClick={() => setMitigationExpanded(!mitigationExpanded)}
            >
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-amber-600" />
                <h4 className="text-base font-semibold text-gray-900">Mitigation Analysis</h4>
                <InfoTooltip text="The insured has a duty to mitigate losses. Failure to take reasonable steps can result in deductions from the claim. Toggle factors to model the mitigation credit." />
              </div>
              <div className="flex items-center space-x-3">
                {mitigationResult && !mitigationExpanded && (
                  <span className="text-sm font-medium text-amber-700 bg-amber-50 px-2.5 py-0.5 rounded-full">
                    Credit: {formatCurrency(mitigationResult.mitigation_credit_amount)} ({mitigationResult.mitigation_credit_pct}%)
                  </span>
                )}
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${mitigationExpanded ? 'rotate-180' : ''}`}
                />
              </div>
            </div>

            {mitigationExpanded && (
              <div className="mt-4 space-y-4">
                {/* Mitigation Error */}
                {mitigationError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{mitigationError}</p>
                  </div>
                )}

                {/* Factor Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Marketing Maintained */}
                  <label className="flex items-center justify-between bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-800">Marketing Maintained</span>
                      <p className="text-xs text-gray-500 mt-0.5">Continued marketing/advertising during loss period</p>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={marketingMaintained}
                        onClick={() => setMarketingMaintained(!marketingMaintained)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors ${marketingMaintained ? 'bg-amber-500' : 'bg-gray-300'}`}
                      >
                        <span className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg transition-transform ${marketingMaintained ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  </label>

                  {/* Temporary Relocation */}
                  <label className="flex items-center justify-between bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-800">Temporary Relocation</span>
                      <p className="text-xs text-gray-500 mt-0.5">Established temporary operations to minimize disruption</p>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={tempRelocation}
                        onClick={() => setTempRelocation(!tempRelocation)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors ${tempRelocation ? 'bg-amber-500' : 'bg-gray-300'}`}
                      >
                        <span className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg transition-transform ${tempRelocation ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  </label>

                  {/* Alternative Revenue */}
                  <label className="flex items-center justify-between bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-800">Alternative Revenue</span>
                      <p className="text-xs text-gray-500 mt-0.5">Pursued alternative revenue streams during loss period</p>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={altRevenue}
                        onClick={() => setAltRevenue(!altRevenue)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors ${altRevenue ? 'bg-amber-500' : 'bg-gray-300'}`}
                      >
                        <span className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg transition-transform ${altRevenue ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  </label>

                  {/* Expense Reduction */}
                  <label className="flex items-center justify-between bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition">
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-800">Expense Reduction</span>
                      <p className="text-xs text-gray-500 mt-0.5">Reduced unnecessary expenses during loss period</p>
                    </div>
                    <div className="ml-3">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={expenseReduction}
                        onClick={() => setExpenseReduction(!expenseReduction)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors ${expenseReduction ? 'bg-amber-500' : 'bg-gray-300'}`}
                      >
                        <span className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg transition-transform ${expenseReduction ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    </div>
                  </label>
                </div>

                {/* Resume Timeline Dropdown */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-gray-800">Operations Resume Timeline</span>
                      <p className="text-xs text-gray-500 mt-0.5">How quickly operations resumed compared to industry standard</p>
                    </div>
                    <select
                      value={resumeTimeline}
                      onChange={(e) => setResumeTimeline(e.target.value)}
                      className="ml-4 text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      <option value="on_time">On Time</option>
                      <option value="delayed">Delayed</option>
                      <option value="significantly_delayed">Significantly Delayed</option>
                    </select>
                  </div>
                </div>

                {/* Mitigation Results */}
                {mitigationLoading && (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                  </div>
                )}

                {mitigationResult && !mitigationLoading && (
                  <div className="space-y-3">
                    {/* Summary Card */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-700 font-medium">Total Mitigation Credit</p>
                          <p className="text-2xl font-bold text-amber-800 mt-1">
                            {formatCurrency(mitigationResult.mitigation_credit_amount)}
                            <span className="text-sm font-normal ml-2">
                              ({mitigationResult.mitigation_credit_pct}% of claim)
                            </span>
                          </p>
                        </div>
                        {mitigationResult.cap_applied && (
                          <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded">
                            Cap Applied ({mitigationResult.max_credit_pct}% max)
                          </span>
                        )}
                      </div>
                      {mitigationResult.industry_adjustment && (
                        <p className="text-xs text-amber-600 mt-2">
                          {mitigationResult.industry_adjustment}
                        </p>
                      )}
                    </div>

                    {/* Factor Breakdown Table */}
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-4 py-2 font-medium text-gray-600">Factor</th>
                            <th className="text-center px-4 py-2 font-medium text-gray-600">Score</th>
                            <th className="text-right px-4 py-2 font-medium text-gray-600">Deduction</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mitigationResult.factors?.map((factor: any, idx: number) => (
                            <tr key={idx} className="border-b border-gray-100 last:border-0">
                              <td className="px-4 py-2">
                                <span className="font-medium text-gray-800">{factor.name}</span>
                                <p className="text-xs text-gray-500 mt-0.5">{factor.note}</p>
                              </td>
                              <td className="text-center px-4 py-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  factor.score === 100
                                    ? 'bg-green-100 text-green-700'
                                    : factor.score === 50
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {factor.score}/100
                                </span>
                              </td>
                              <td className="text-right px-4 py-2 font-medium text-red-600">
                                {factor.deduction > 0 ? `-${formatCurrency(factor.deduction)}` : '$0.00'}
                                <span className="text-xs text-gray-500 ml-1">({factor.deduction_pct}%)</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Methodology Note */}
                    <p className="text-xs text-gray-500 italic">
                      {mitigationResult.methodology}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
